import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { EnvironmentHelper } from "@repo/helpers";
import { LocalStorageKeys } from "@repo/logic";
import { addYears } from "date-fns";
import { createContext, type FC, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { type cookieId, cookies } from "../components/Drawer/CookieDrawer/CookieIds";
import { updateGTMConsents } from "../gtm";
import { usePrimaryAPIClient } from "./PrimaryAPIClient";

export type ICookieConsent = {
    id: string;
    consentSet: boolean;
    allowedCookies: cookieId[];
    expires?: string;
};

type IConsentInfoContext = {
    data: ICookieConsent;
    setCookieConsent: (allowed: cookieId[]) => Promise<void>;
    displayCookieForm: boolean;
    setDisplayCookieForm: (value: boolean) => void;
    isError: boolean;
    isLoading: boolean;
};
const CookieContext = createContext<IConsentInfoContext>("");

const defaultConsent: ICookieConsent = {
    id: uuidv4(),
    consentSet: false,
    allowedCookies: [],
};

const LoadConsentFromLocal = () => {
    if (EnvironmentHelper.isBrowser()) {
        const consentJson = localStorage.getItem(LocalStorageKeys.COOKIE_CONSENT);
        if (!consentJson) {
            localStorage.setItem(LocalStorageKeys.COOKIE_CONSENT, JSON.stringify(defaultConsent));
            return defaultConsent;
        }
        const consent: ICookieConsent = JSON.parse(consentJson);
        return consent;
    }
    return defaultConsent;
};

const now = new Date().toISOString();

export const useCookieConsents = (): IConsentInfoContext => {
    return useContext(CookieContext);
};

export const CookieContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [displayCookieForm, setDisplayCookieForm] = useState(false);
    const [consent, setConsent] = useState(LoadConsentFromLocal());
    const ai = useAppInsightsContext();
    const { consentApi } = usePrimaryAPIClient();
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const localConsent = consent;
        if (localConsent.consentSet && localConsent.expires && localConsent.expires < now) {
            localStorage.setItem(
                LocalStorageKeys.COOKIE_CONSENT,
                JSON.stringify({ ...defaultConsent, id: localConsent.id }),
            );
            setConsent(defaultConsent);
        } else {
            setConsent(localConsent);
        }
    }, []);

    const setCookieConsent = async (allowed: cookieId[]) => {
        if (allowed.includes(cookies.appInsights)) {
            const cookieManager = ai.getCookieMgr();
            cookieManager.setEnabled(true);
        }

        updateGTMConsents(allowed.includes(cookies.googleAnalytics), allowed.includes(cookies.googleAds));

        const date = addYears(Date.now(), 1).toISOString();

        const reqBody = {
            id: consent.id,
            cookieConsent: allowed.join(", "),
            expirationDate: date,
        };

        try {
            setIsLoading(true);
            await consentApi.setCookiesConsentsPut({
                setCookieConsentRequest: reqBody,
            });
            localStorage.setItem(
                LocalStorageKeys.COOKIE_CONSENT,
                JSON.stringify({
                    id: consent.id,
                    consentSet: true,
                    allowedCookies: allowed,
                    expires: date,
                }),
            );
            setConsent(LoadConsentFromLocal());
            setIsError(false);
            setIsLoading(false);
        } catch {
            setIsLoading(false);
            setIsError(true);
            setConsent({
                id: consent.id,
                consentSet: true,
                allowedCookies: allowed,
            });
        }
        setDisplayCookieForm(false);
    };

    return (
        <CookieContext.Provider
            value={{
                data: consent,
                setCookieConsent,
                displayCookieForm,
                setDisplayCookieForm,
                isError,
                isLoading,
            }}
        >
            {children}
        </CookieContext.Provider>
    );
};
